import { useLocation } from "wouter";
import { useEffect } from "react";
// Import the Azure auth hook instead of the Clerk one
import { useAuthProvider } from "@/hooks/use-azure-auth-provider";

// Import the Azure Sign In button
import { AzureSignInButton } from "@/components/auth/AzureAuthButtons";

export default function WelcomePage() {
  const { isSignedIn, isLoaded } = useAuthProvider();
  const [, navigate] = useLocation();

  useEffect(() => {
    // If user is already signed in, redirect to the main app
    if (isLoaded && isSignedIn) {
      navigate("/field-mappings");
    }
  }, [isSignedIn, isLoaded, navigate]);
  
  // If auth is still loading, show a better loading indicator
  if (!isLoaded) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4"></div>
          <p className="text-lg">Loading authentication...</p>
          <p className="text-sm text-gray-500 mt-2">Please wait while we initialize the authentication system</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen">
      <header className="px-4 lg:px-6 h-14 flex items-center">
        <div className="ml-auto">
          {/* Use Azure Sign In button */}
          <div className="auth-button-container">
            <AzureSignInButton />
          </div>
        </div>
      </header>
      <main className="flex-1">
        <section className="w-full py-12 md:py-24 lg:py-32 xl:py-48">
          <div className="container px-4 md:px-6">
            <div className="grid gap-2 lg:grid-cols-[1fr_400px] lg:gap-12 xl:grid-cols-[1fr_600px]">
              <div className="flex flex-col justify-center space-y-4">
                <div className="space-y-2">
                  <h1 className="text-3xl font-bold tracking-tighter sm:text-5xl xl:text-6xl/none">
                    Publishing Nexus
                  </h1>
                  <p className="max-w-[600px] text-gray-500 md:text-xl dark:text-gray-400">
                   Welcome to Publishing Nexus from Peter Lang</p>
                   <p>
                   Manage your publishing world with ease.
                  </p>
                </div>
                {/* Removed Get Started button */}
              </div>
              <div className="flex justify-center">
                <img
                  alt="Dataverse Mapping Illustration"
                  className="object-cover border border-gray-200 shadow-sm rounded-xl"
                  src="https://images.unsplash.com/photo-1563986768609-322da13575f3?q=80&w=1000"
                  style={{
                    objectFit: "cover",
                    height: "400px",
                    width: "100%"
                  }}
                />
              </div>
            </div>
            <div className="py-12">
              <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
                <div className="flex flex-col items-center space-y-2 p-4 border rounded-lg shadow-sm">
                  <div className="p-2 bg-primary/10 rounded-full">
                    <svg
                      className="h-6 w-6 text-primary"
                      fill="none"
                      height="24"
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                      width="24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
                      <path d="M15 2H9a1 1 0 0 0-1 1v2a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V3a1 1 0 0 0-1-1Z" />
                      <path d="M12 11h4" />
                      <path d="M12 16h4" />
                      <path d="M8 11h.01" />
                      <path d="M8 16h.01" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-bold">Field Mappings</h3>
                  <p className="text-gray-500 text-center dark:text-gray-400">
                    Create and manage mappings between Dataverse fields and your custom system.
                  </p>
                </div>
                <div className="flex flex-col items-center space-y-2 p-4 border rounded-lg shadow-sm">
                  <div className="p-2 bg-primary/10 rounded-full">
                    <svg
                      className="h-6 w-6 text-primary"
                      fill="none"
                      height="24"
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                      width="24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="m6 16 6-12 6 12" />
                      <path d="M8 12h8" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-bold">Auto-Discovery</h3>
                  <p className="text-gray-500 text-center dark:text-gray-400">
                    Automatically discover and suggest field mappings for selected entities.
                  </p>
                </div>
                <div className="flex flex-col items-center space-y-2 p-4 border rounded-lg shadow-sm">
                  <div className="p-2 bg-primary/10 rounded-full">
                    <svg
                      className="h-6 w-6 text-primary"
                      fill="none"
                      height="24"
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                      width="24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M3 12a9 9 0 1 0 18 0 9 9 0 0 0-18 0" />
                      <path d="M12 7v5l3 3" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-bold">Sync Logs</h3>
                  <p className="text-gray-500 text-center dark:text-gray-400">
                    Monitor synchronization history and troubleshoot integration issues.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <footer className="flex flex-col gap-2 sm:flex-row py-6 w-full shrink-0 items-center px-4 md:px-6 border-t">
        <p className="text-xs text-gray-500 dark:text-gray-400">
          © {new Date().getFullYear()} Dataverse Field Mapping Tool. All rights reserved.
        </p>
        <nav className="sm:ml-auto flex gap-4 sm:gap-2">
          <a href="#" className="text-xs hover:underline underline-offset-4">
            Terms of Service
          </a>
          <a href="#" className="text-xs hover:underline underline-offset-4">
            Privacy
          </a>
        </nav>
      </footer>
    </div>
  );
}